import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AUTH SERVICE - Real Backend Integration
 * Handles all authentication-related API calls
 */

class AuthService {
  static instance = null;

  static getInstance() {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Register a new user
   */
  async signup(data) {
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        timezone: data.timezone,
        password: data.password
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      return { success: false, message };
    }
  }

  /**
   * Login user
   */
  async login(email, password) {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Store token and user data
      localStorage.setItem('preciseo_token', response.data.access_token);
      localStorage.setItem('preciseo_user', JSON.stringify(response.data.user));
      
      return { success: true, user: response.data.user, message: 'Login successful.' };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'UNVERIFIED') {
        return { success: false, message: 'UNVERIFIED' };
      }
      return { success: false, message: detail || 'Invalid credentials.' };
    }
  }

  /**
   * Verify email token
   */
  async verifyToken(token) {
    try {
      const response = await axios.post(`${API}/auth/verify/${token}`);
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired verification link.';
      return { success: false, message };
    }
  }

  /**
   * Resend verification email
   */
  async resendVerification(email) {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend verification email.';
      return { success: false, message };
    }
  }

  /**
   * Request password reset
   */
  async forgotPassword(email) {
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: true, message: 'If that email exists, a reset link has been sent.' };
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(token, newPass) {
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        password: newPass
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired reset link.';
      return { success: false, message };
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser() {
    const token = localStorage.getItem('preciseo_token');
    if (!token) return null;

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update stored user data
      localStorage.setItem('preciseo_user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      this.logout();
      return null;
    }
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem('preciseo_token');
    localStorage.removeItem('preciseo_user');
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn() {
    return !!localStorage.getItem('preciseo_token');
  }

  /**
   * Get stored user
   */
  getStoredUser() {
    const user = localStorage.getItem('preciseo_user');
    return user ? JSON.parse(user) : null;
  }

  /**
   * Get auth token
   */
  getToken() {
    return localStorage.getItem('preciseo_token');
  }
}

export const authApi = AuthService.getInstance();
